"use client";

import { MenuProps } from "@/lib/wordpress/_types/blocksStyle";
import {
  Accordion,
  AccordionItem,
  Button,
  clickOutSideToClose,
  Dropdown,
  DropdownItem,
  Heading,
  Icon,
  RichText,
  Row,
  SocialLinks,
  variables,
} from "daskapital-ds";
import {
  StyledHamburger,
  StyledHeader,
  StyledMenu,
  StyledNav,
  StyledNavMobile,
} from "./Header.theme";
import { useState } from "react";
import DisplayImage from "../DisplayImage/DisplayImage";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import Link from "next/link";
import Logo from "../Logo/Logo";
import React from "react";
import { removeScroll } from "@/lib/helpers/scrollHelpers";
import { styled } from "styled-components";
import { useGlobalContext } from "@/lib/globalContext";
import { useSearchParams } from "next/navigation";

const StyledCell = styled.div`
  flex-grow: 1;
  flex-shrink: 0;

  @media only screen and (min-width: 1280px) {
    flex-grow: 0;
    width: 20%;
  }
`;

const StyledLogoCell = styled(StyledCell)`
  @media only screen and (min-width: 1280px) {
    width: 15%;
  }
`;

const OutlineButton = styled(Button)`
  .style-light & {
    color: ${variables.colors.primary50};
    box-shadow: 0 0 0 1px ${variables.colors.primary50} inset;

    &:before {
      background: ${variables.colors.primary50};
    }

    &:hover {
      color: ${variables.colors.white} !important;
    }
  }

  /* header-absolute floats over the hero (or sits scrolled-dark) — it should
     always look like style-dark, even on pages whose page_style is light */
  .header-absolute & {
    color: ${variables.colors.white};
    box-shadow: 0 0 0 1px ${variables.colors.white} inset;

    &:before {
      background: ${variables.colors.white};
    }

    &:hover {
      color: ${variables.colors.primary50} !important;
    }
  }

  &:hover {
    color: ${variables.colors.primary50} !important;
  }
`;

const Header = () => {
  const state = useGlobalContext();
  const searchParams = useSearchParams();
  const [menuActive, setMenuActive] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);

  const [mobileAccordion, setMobileAccordion] = useState<string | null>();
  const handleMobileAccordion = (id: string) => {
    if (mobileAccordion === id) {
      setMobileAccordion(null);
      return;
    }
    setMobileAccordion(id);
  };
  const headerOnClick = (isActive: boolean, search: boolean) => {
    removeScroll(isActive, state);

    if (search) {
      menuActive && setMenuActive(false);
      setSearchActive(isActive);
    } else {
      searchActive && setSearchActive(false);
      setMenuActive(isActive);
    }
  };

  // Without menus (WordPress unavailable) the header still renders the logo,
  // language switcher and login buttons; the nav lists are guarded below.
  return (
    <>
      <StyledHeader id="masthead" $active={menuActive}>
        <Row wrap="nowrap" align="flex-start" className="pt-2 pb-2">
          <StyledLogoCell>
            <Logo />
          </StyledLogoCell>
          <StyledNav>
            {state?.menus &&
              state?.menus[`primary-menu`]?.map((item: MenuProps, key) => {
                return item.menu_items.length === 0 ? (
                  <Link
                    key={`nav_menu_li_${item.id}`}
                    className="d-flex align-items-center"
                    href={item.url || ""}
                    target={item.target ? (item.target as string) : undefined}
                    onClick={() => headerOnClick(false, false)}
                  >
                    <RichText style={{ whiteSpace: "nowrap" }} tag={"p"}>
                      {item.title}
                    </RichText>
                  </Link>
                ) : (
                  <Dropdown
                    title={
                      item.url && item.url !== "#" ? (
                        <Link
                          href={item.url}
                          target={
                            item.target ? (item.target as string) : undefined
                          }
                          onClick={() => headerOnClick(false, false)}
                          style={{ padding: 0 }}
                        >
                          <RichText tag={"p"}>{item.title}</RichText>
                        </Link>
                      ) : (
                        <RichText tag={"p"}>{item.title}</RichText>
                      )
                    }
                    icon={<Icon icon="icon-chevron-down" />}
                    key={`nav_menu_li_${item.id}`}
                  >
                    {item.menu_items.map((subItem: MenuProps) => {
                      return (
                        <DropdownItem key={`nav_menu_li_${subItem.id}`}>
                          <Link
                            href={subItem.url || ""}
                            target={
                              subItem.target
                                ? (subItem.target as string)
                                : undefined
                            }
                            onClick={() => {
                              headerOnClick(false, true);
                              setOpenSubmenu(null);
                            }}
                          >
                            <RichText
                              style={{ whiteSpace: "nowrap" }}
                              tag={"p"}
                            >
                              {subItem.title}
                            </RichText>
                          </Link>
                        </DropdownItem>
                      );
                    })}
                  </Dropdown>
                );
              })}
          </StyledNav>
          <StyledCell className="d-inline-flex align-items-center justify-content-end">
            <div className="align-center d-none d-xl-inline-flex">
              <LanguageSwitcher></LanguageSwitcher>
              <OutlineButton
                outlined
                href="https://platform.daskapital.eu/login"
                target="_blank"
                className="align-middle ms-2"
              >
                {state?.dict?.custom?.LogIn || "Login"}
              </OutlineButton>

              <Button
                variant={"secondary"}
                href="https://platform.daskapital.eu/register"
                target="_blank"
                className="align-middle"
              >
                {state?.dict?.custom?.signIn || "Register"}
              </Button>
            </div>

            <Link
              href="https://platform.daskapital.eu/login"
              target="_blank"
              className="texts align-middle d-inline-flex d-xl-none"
            >
              {state?.dict?.custom?.LogIn || "Login"}
            </Link>

            <Link
              href={"https://platform.daskapital.eu/register"}
              target="_blank"
              className="texts align-middle d-inline-flex d-xl-none ms-4"
            >
              {state?.dict?.custom?.signIn || "Register"}
            </Link>
            <StyledHamburger
              className="d-inline-flex d-xl-none ms-4"
              $active={menuActive}
              onClick={() => headerOnClick(!menuActive, false)}
            >
              <span></span>
              <span></span>
            </StyledHamburger>
          </StyledCell>
        </Row>
      </StyledHeader>
      <StyledMenu $active={menuActive} data-lenis-prevent={true}>
        <div onClick={() => headerOnClick(false, false)}></div>
        <div>
          <StyledNavMobile>
            {state?.menus &&
              state?.menus[`primary-menu`]?.map((item: MenuProps) => {
                return item.menu_items.length === 0 ? (
                  <Link
                    key={`nav_menu_li_${item.id}`}
                    className="d-flex align-items-center"
                    href={item.url || ""}
                    target={item.target ? (item.target as string) : undefined}
                    onClick={() => headerOnClick(false, false)}
                  >
                    <Heading tag="span" size="h3">
                      {item?.title as string}
                    </Heading>
                  </Link>
                ) : (
                  <div key={`nav_menu_li_${item.id}`}>
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "8px" }}
                      onClick={() => handleMobileAccordion(item.id as string)}
                    >
                      <Heading tag="span" size="h3">
                        {item?.title as string}
                      </Heading>
                      <Icon
                        icon="icon-chevron-down"
                        style={{
                          transform: `rotate(${mobileAccordion === item.id ? 180 : 0}deg)`,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: mobileAccordion === item.id ? "block" : "none",
                      }}
                    >
                      {item.url && item.url !== "#" && (
                        <Link
                          key={`nav_menu_li_${item.id}_self`}
                          href={item.url}
                          target={
                            item.target ? (item.target as string) : undefined
                          }
                          onClick={() => headerOnClick(false, false)}
                          style={{
                            textIndent: "12px",
                            margin: "12px 0",
                            display: "block",
                          }}
                        >
                          <RichText tag="p">{item?.title as string}</RichText>
                        </Link>
                      )}
                      {item.menu_items.map((subItem: MenuProps) => {
                        return (
                          <Link
                            key={`nav_menu_li_${subItem.id}`}
                            href={subItem.url || ""}
                            target={
                              subItem.target
                                ? (subItem.target as string)
                                : undefined
                            }
                            onClick={() => headerOnClick(false, false)}
                            style={{
                              textIndent: "12px",
                              margin: "12px 0",
                              display: "block",
                            }}
                          >
                            <RichText tag="p">
                              {subItem?.title as string}
                            </RichText>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </StyledNavMobile>
          <div>
            <LanguageSwitcher></LanguageSwitcher>
          </div>

          <SocialLinks
            socials={state.themeOptions?.socials as any}
            style={{ flexDirection: "row" }}
            className={"d-flex align-items-start mt-6"}
          />
          <DisplayImage
            alt={"menu_bg"}
            url={"/assets/components/menu_mobile.svg"}
            fill={true}
          />
        </div>
      </StyledMenu>
      {/* <StyledSearch
        ref={searchContainerRef}
        $active={searchActive}
        data-lenis-prevent={true}
      >
        <div>
          <CloseButton
            variant="sm"
            onClick={() => headerOnClick(!searchActive, true)}
          ></CloseButton>
          <SearchForm
            value={searchParams.get("q") as string}
            onClose={() => headerOnClick(!searchActive, true)}
          />
        </div>
      </StyledSearch> */}
    </>
  );
};

export default Header;
